import React, { useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, Upload, Palette } from 'lucide-react';
import { AuthContext } from '@/App';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL;

export default function BrandCreate() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#06b6d4');
  const [secondaryColor, setSecondaryColor] = useState('#8b5cf6');
  const [ctaText, setCtaText] = useState('Get Started');
  const [ctaUrl, setCtaUrl] = useState('/signup');
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Brand name is required');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/brands`,
        {
          name: name.trim(),
          logo_url: logoUrl || null,
          primary_color: primaryColor,
          secondary_color: secondaryColor,
          cta_default_text: ctaText,
          cta_default_url: ctaUrl
        },
        { withCredentials: true }
      );
      
      toast.success('Brand created successfully!');
      navigate('/brands');
    } catch (err) {
      console.error('Error creating brand:', err);
      toast.error(err.response?.data?.detail || 'Failed to create brand');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Create New Brand</h1>
        <p className="text-gray-400">Set up a new brand/app with isolated content and settings</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Brand Name */}
        <div className="bg-[#1a1d23] rounded-lg p-6 border border-gray-800">
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Brand/App Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., My Awesome App"
            className="w-full px-4 py-3 bg-[#121417] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-cyan-600"
            required
          />
          <p className="text-xs text-gray-500 mt-2">
            A folder-safe slug will be auto-generated from this name
          </p>
        </div>

        {/* Logo URL */}
        <div className="bg-[#1a1d23] rounded-lg p-6 border border-gray-800">
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Logo URL (Optional)
          </label>
          <div className="flex items-center gap-3">
            <Upload className="w-5 h-5 text-gray-500" />
            <input
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
              className="flex-1 px-4 py-3 bg-[#121417] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-cyan-600"
            />
          </div>
          {logoUrl && (
            <div className="mt-4 flex items-center gap-3">
              <img src={logoUrl} alt="Logo preview" className="w-16 h-16 object-contain rounded-lg bg-gray-800" />
              <span className="text-sm text-gray-400">Preview</span>
            </div>
          )}
        </div>

        {/* Brand Colors */}
        <div className="bg-[#1a1d23] rounded-lg p-6 border border-gray-800">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-white">Brand Colors</h3>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Primary Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1 px-3 py-2 bg-[#121417] border border-gray-800 rounded text-white text-sm focus:outline-none focus:border-cyan-600"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Secondary Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="flex-1 px-3 py-2 bg-[#121417] border border-gray-800 rounded text-white text-sm focus:outline-none focus:border-cyan-600"
                />
              </div>
            </div>
          </div>
          
          {/* Color Preview */}
          <div className="mt-4 p-4 bg-[#121417] rounded-lg">
            <p className="text-xs text-gray-500 mb-2">Preview:</p>
            <div className="flex items-center gap-3">
              <div
                className="w-20 h-20 rounded-lg"
                style={{ backgroundColor: primaryColor }}
              />
              <div
                className="w-20 h-20 rounded-lg"
                style={{ backgroundColor: secondaryColor }}
              />
            </div>
          </div>
        </div>

        {/* Default CTA */}
        <div className="bg-[#1a1d23] rounded-lg p-6 border border-gray-800">
          <h3 className="text-lg font-semibold text-white mb-4">Default Call-to-Action</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                CTA Text
              </label>
              <input
                type="text"
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                placeholder="Get Started"
                className="w-full px-4 py-3 bg-[#121417] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-cyan-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                CTA URL
              </label>
              <input
                type="text"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                placeholder="/signup"
                className="w-full px-4 py-3 bg-[#121417] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-cyan-600"
              />
            </div>
            
            {/* CTA Preview */}
            <div className="mt-4 p-4 bg-[#121417] rounded-lg">
              <p className="text-xs text-gray-500 mb-2">Preview:</p>
              <button
                type="button"
                className="px-6 py-3 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: primaryColor,
                  color: '#ffffff'
                }}
              >
                {ctaText}
              </button>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between pt-6">
          <button
            type="button"
            onClick={() => navigate('/brands')}
            className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={creating}
            className="px-8 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {creating ? 'Creating...' : 'Create Brand'}
          </button>
        </div>
      </form>
    </div>
  );
}
